import { EditorView, basicSetup } from 'codemirror';
import { StreamLanguage } from '@codemirror/language';
import { lua } from '@codemirror/legacy-modes/mode/lua';
import { Camera } from './camera';
import { Game } from './game';
import { PatchMap, TileMap } from './tile';
import * as Util from './util';
import { Renderer } from './render';
import { CHAR_WIDTH } from './constants';
var EditorState;
(function (EditorState) {
    EditorState[EditorState["Brush"] = 0] = "Brush";
    EditorState[EditorState["Pipette"] = 1] = "Pipette";
})(EditorState || (EditorState = {}));
const TabDrawTile = "draw-tile";
const TabDrawPatch = "draw-patch";
const TabDrawMarker = "draw-marker";
const pointToString = (point) => `${point.x},${point.y}`;
export class Editor {
    // Code
    scriptInput;
    // Canvas
    canvas;
    // Info
    infoTitle;
    infoDescription;
    // Tabs
    tileMapTab;
    // Settings
    widthInput;
    heightInput;
    patchCountInput;
    patchWidthInput;
    patchHeightInput;
    applySettingsButton;
    // Draw Tilemap
    charInput;
    colourInput;
    invertedInput;
    invertedLabel;
    pipetteButton;
    // Draw Patchmap
    patchIdInput;
    // Draw Markers
    markerIdInput;
    // Physics
    solidCharInput;
    // Camera
    leftButton;
    upButton;
    rightButton;
    downButton;
    // Import/Export
    exportButton;
    importButton;
    // Other
    renderer;
    heldDown;
    patchCamera;
    tileCamera;
    tileMap;
    patchMap;
    markers;
    state;
    constructor(inputGame) {
        this.state = EditorState.Brush;
        //Code
        const codeContent = document.getElementById('tab-content-code');
        this.scriptInput = new EditorView({
            extensions: [basicSetup, StreamLanguage.define(lua)],
            parent: codeContent
        });
        //Canvas
        this.canvas = document.getElementById('editor-canvas');
        //Info
        this.infoTitle = document.getElementById('info-title');
        this.infoDescription = document.getElementById('info-description');
        //Tabs
        this.tileMapTab = document.getElementById('tilemap-tab');
        //Settings
        this.widthInput = document.getElementById('tilemap-width');
        this.heightInput = document.getElementById('tilemap-height');
        this.patchCountInput = document.getElementById('patch-count');
        this.patchWidthInput = document.getElementById('patch-width');
        this.patchHeightInput = document.getElementById('patch-height');
        this.applySettingsButton = document.getElementById('tilemap-settings-apply');
        //Drawing
        this.charInput = document.getElementById('editor-char-input');
        this.colourInput = document.getElementById('editor-colour-input');
        this.invertedInput = document.getElementById('editor-invert-input');
        this.invertedLabel = document.getElementById('editor-invert-label');
        this.pipetteButton = document.getElementById('editor-pipette-button');
        //Patch Drawing
        this.patchIdInput = document.getElementById('patch-id');
        //Marker Drawing
        this.markerIdInput = document.getElementById('marker-id');
        //Physics
        this.solidCharInput = document.getElementById('editor-solid-input');
        //Camera
        this.leftButton = document.getElementById('left-button');
        this.upButton = document.getElementById('up-button');
        this.rightButton = document.getElementById('right-button');
        this.downButton = document.getElementById('down-button');
        //Import/Export
        this.exportButton = document.getElementById('export-button');
        this.importButton = document.getElementById('import-button');
        this.renderer = new Renderer(this.canvas);
        this.heldDown = false;
        this.patchCamera = new Camera();
        this.tileCamera = new Camera();
        // Place tile while pointer is held
        this.canvas.addEventListener('pointerdown', (event) => {
            this.heldDown = true;
            if (this.tileMapTab.currentTab === TabDrawTile) {
                if (this.state === EditorState.Brush) {
                    this.setTileFromEvent(event);
                }
                else {
                    this.setBrushFromEvent(event);
                }
            }
            else if (this.tileMapTab.currentTab === TabDrawPatch) {
                this.setPatchFromEvent(event);
            }
            else if (this.tileMapTab.currentTab === TabDrawMarker) {
                this.setMarkerFromEvent(event);
            }
        });
        window.addEventListener('pointerup', (event) => {
            this.heldDown = false;
            if (this.tileMapTab.currentTab === TabDrawTile) {
                if (this.state === EditorState.Brush) {
                }
                else {
                    this.state = EditorState.Brush;
                }
            }
            else if (this.tileMapTab.currentTab === TabDrawPatch) {
            }
        });
        this.canvas.addEventListener('pointermove', (event) => {
            if (this.heldDown) {
                if (this.tileMapTab.currentTab === TabDrawTile) {
                    if (this.state === EditorState.Brush) {
                        this.setTileFromEvent(event);
                    }
                }
                else if (this.tileMapTab.currentTab === TabDrawPatch) {
                    this.setPatchFromEvent(event);
                }
                else if (this.tileMapTab.currentTab === TabDrawMarker) {
                    this.setMarkerFromEvent(event);
                }
            }
        });
        // Nuke default touch behaviour (pull down screen to reload)
        this.canvas.addEventListener('touchstart', (event) => event.preventDefault(), { passive: false });
        this.canvas.addEventListener('touchend', (event) => event.preventDefault(), { passive: false });
        this.canvas.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false });
        this.tileMapTab.updateListeners.push(function (currentTab) {
        });
        this.applySettingsButton.onclick = () => {
            // Make new tilemap with new size and copy data
            const patchDim = {
                w: this.getAndValidateInputNumber(this.patchWidthInput, 1, 32, 1),
                h: this.getAndValidateInputNumber(this.patchHeightInput, 1, 32, 1),
            };
            const newPatchCount = this.getAndValidateInputNumber(this.patchCountInput, 1, 128, 1);
            const newTileMap = new TileMap(patchDim, newPatchCount);
            for (let i = 0; i < newPatchCount; i++) {
                for (let y = 0; y < patchDim.h; y++) {
                    for (let x = 0; x < patchDim.w; x++) {
                        const coords = { x: x, y: y };
                        const getTileResult = this.tileMap.getTile(coords, i);
                        if (getTileResult !== null) {
                            newTileMap.setTile(getTileResult, coords, i);
                        }
                    }
                }
            }
            this.tileMap = newTileMap;
            this.tileCamera.setLevelDim(this.tileMap.getDrawDim());
            const newDim = {
                w: this.getAndValidateInputNumber(this.widthInput, 1, 128, 1),
                h: this.getAndValidateInputNumber(this.heightInput, 1, 128, 1),
            };
            const newPatchMap = new PatchMap(newDim);
            for (let y = 0; y < newDim.h; y++) {
                for (let x = 0; x < newDim.w; x++) {
                    const coords = { x: x, y: y };
                    const getPatchResult = this.patchMap.getPatch(coords);
                    if (getPatchResult !== null) {
                        newPatchMap.setPatch(getPatchResult, coords);
                    }
                }
            }
            this.patchMap = newPatchMap;
            this.patchCamera.setLevelDim(this.patchMap.getDrawDim(this.tileMap));
        };
        this.leftButton.onclick = () => { this.getCurrentCamera().pos.x -= 64; };
        this.upButton.onclick = () => { this.getCurrentCamera().pos.y -= 64; };
        this.rightButton.onclick = () => { this.getCurrentCamera().pos.x += 64; };
        this.downButton.onclick = () => { this.getCurrentCamera().pos.y += 64; };
        this.exportButton.onclick = () => {
            let serialised = JSON.stringify({ tileMap: this.tileMap, patchMap: this.patchMap, markers: Array.from(this.markers.values()) });
            navigator.clipboard.writeText(serialised);
        };
        this.importButton.onclick = async () => {
            try {
                let serialised = JSON.parse(await navigator.clipboard.readText());
                this.tileMap = TileMap.Copy(serialised.tileMap);
                this.patchMap = PatchMap.Copy(serialised.patchMap);
                this.markers = new Map();
                for (const value of serialised.markers) {
                    this.markers.set(pointToString(value), value);
                }
            }
            catch (err) {
                alert("Failed to load tilemap from clipboard, are you sure it is in the clipboard and correctly formatted?");
            }
        };
        this.invertedLabel.classList.toggle("hidden", !this.invertedInput.checked);
        this.invertedInput.onchange = () => {
            this.invertedLabel.classList.toggle("hidden", !this.invertedInput.checked);
        };
        this.pipetteButton.onclick = () => {
            this.state = EditorState.Pipette;
        };
        // Load input game into editor
        this.infoTitle.value = inputGame.metadata.title;
        this.infoDescription.value = inputGame.metadata.description;
        const transaction = this.scriptInput.state.update({ changes: {
                from: 0,
                to: this.scriptInput.state.doc.length,
                insert: inputGame.script
            } });
        this.scriptInput.update([transaction]);
        this.tileMap = TileMap.Copy(inputGame.tileMap);
        this.patchMap = PatchMap.Copy(inputGame.patchMap);
        this.patchWidthInput.valueAsNumber = this.tileMap.dim.w;
        this.patchHeightInput.valueAsNumber = this.tileMap.dim.h;
        this.patchCountInput.valueAsNumber = this.tileMap.count;
        this.widthInput.valueAsNumber = this.patchMap.dim.w;
        this.heightInput.valueAsNumber = this.patchMap.dim.h;
        this.patchCamera.setLevelDim(this.patchMap.getDrawDim(this.tileMap));
        this.tileCamera.setLevelDim(this.tileMap.getDrawDim());
        this.solidCharInput.value = String.fromCodePoint(...inputGame.solidTiles);
        this.markers = new Map();
        for (const value of inputGame.markers) {
            this.markers.set(pointToString(value), value);
        }
        this.renderer.startRenderLoop(() => this.draw());
    }
    getAndValidateInputNumber(input, min, max, step) {
        let value = input.valueAsNumber;
        value = Util.clamp(Math.ceil(value / step) * step, min, max);
        input.valueAsNumber = value;
        return value;
    }
    getCoordFromEvent(event) {
        let pixel = Util.getPointerPos(this.canvas, event);
        let viewOffset = this.getCurrentCamera().getViewOffset();
        pixel.x -= viewOffset.x;
        pixel.y -= viewOffset.y;
        return Util.pixelToTile(pixel);
    }
    setTileFromEvent(event) {
        // Get array of codepoints
        let codePoints = Util.stringToCodePoints(this.charInput.value);
        let colour = parseInt(this.colourInput.value);
        const inverted = this.invertedInput.checked;
        if (inverted) {
            colour += 8;
        }
        if (codePoints.length == 0) {
            // Erase
            codePoints = [' '.codePointAt(0)];
        }
        // Draw array to tilemap
        let pair = this.tileMap.getSplitCoords(this.getCoordFromEvent(event));
        for (const codePoint of codePoints) {
            this.tileMap.setTile({ codePoint: codePoint, colour: colour }, pair.coords, pair.patchIndex);
            pair.coords.x++;
        }
    }
    setPatchFromEvent(event) {
        const point = this.getCoordFromEvent(event);
        point.x = Math.floor(point.x / this.tileMap.dim.w);
        point.y = Math.floor(point.y / this.tileMap.dim.h);
        this.patchMap.setPatch({ patchId: this.patchIdInput.valueAsNumber, transform: 0 }, point);
    }
    setMarkerFromEvent(event) {
        const point = this.getCoordFromEvent(event);
        if (this.markerIdInput.value.length > 0) {
            this.markers.set(pointToString(point), { ...point, codePoint: this.markerIdInput.value.codePointAt(0) ?? 0 });
        }
        else {
            this.markers.delete(pointToString(point));
        }
    }
    setBrushFromEvent(event) {
        let pair = this.tileMap.getSplitCoords(this.getCoordFromEvent(event));
        let tileData = this.tileMap.getTile(pair.coords, pair.patchIndex);
        if (tileData !== null) {
            this.charInput.value = String.fromCodePoint(tileData.codePoint);
            this.colourInput.value = (tileData.colour % 8).toString();
            this.invertedInput.checked = tileData.colour > 8;
        }
    }
    #isPatchMapView() {
        return this.tileMapTab.currentTab === TabDrawPatch || this.tileMapTab.currentTab === TabDrawMarker;
    }
    getCurrentCamera() {
        return this.#isPatchMapView() ? this.patchCamera : this.tileCamera;
    }
    draw() {
        this.renderer.beginFrame();
        this.renderer.viewOffset = this.getCurrentCamera().getViewOffset();
        if (this.#isPatchMapView()) {
            this.patchMap.draw(this.renderer, this.tileMap, true);
        }
        else {
            this.tileMap.draw(this.renderer, true);
        }
        if (this.tileMapTab.currentTab === TabDrawMarker) {
            for (const m of this.markers.values()) {
                this.renderer.drawBox(m.x * CHAR_WIDTH, m.y * CHAR_WIDTH, (m.x + 1) * CHAR_WIDTH, (m.y + 1) * CHAR_WIDTH, -2.5);
                this.renderer.drawCharacters([m.codePoint], [0], m.x * CHAR_WIDTH, m.y * CHAR_WIDTH, 0, 0, 0, false, false);
            }
        }
        this.renderer.endFrame();
    }
    getGame() {
        let game = new Game({
            title: this.infoTitle.value,
            description: this.infoDescription.value
        }, this.scriptInput.state.doc.toString(), this.tileMap, this.patchMap);
        game.markers = Array.from(this.markers.values());
        game.solidTiles = Util.stringToCodePoints(this.solidCharInput.value);
        return game;
    }
}
